import { useEffectEvent, type ReactNode } from "react";
import type { CmmdProjectAgentMember, CmmdTaskWorkflow } from "@cmmd-forge/contracts";
import type { ProjectTaskCreateStatusSelection } from "./projectTaskCreateOptions";
import { Dialog, DialogPopup } from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { ProjectLeadTaskWorkbench } from "./projectLeadCreation/ProjectLeadTaskWorkbench";
import type { useProjectLeadTaskDraft } from "./projectLeadCreation/useProjectLeadTaskDraft";
import { useUiStateStore } from "~/uiStateStore";

type DraftController = ReturnType<typeof useProjectLeadTaskDraft>;

export function isCreateFormDisabled(
  creationDisabled: boolean,
  draft: DraftController,
  pendingRetryAvailable: boolean,
) {
  return (
    creationDisabled ||
    (draft.pendingCreate && pendingRetryAvailable) ||
    (draft.draft !== null && draft.busy)
  );
}

export function createProjectLeadCloseHandler(input: {
  readonly draft: DraftController;
  readonly pendingRetryAvailable: boolean;
  readonly onClose: () => void;
}) {
  return () => {
    if (input.draft.busy || (input.draft.pendingCreate && input.pendingRetryAvailable)) return;
    void (async () => {
      if (input.draft.draft !== null && input.draft.dirty && !(await input.draft.save())) return;
      input.onClose();
    })();
  };
}

export function handleDialogClose(open: boolean, requestClose: () => void): void {
  if (!open) requestClose();
}

export function ActiveProjectLeadWorkbench(props: {
  readonly draft: DraftController;
  readonly projectTitle: string;
  readonly onClose: () => void;
}) {
  const closeLatest = useEffectEvent(props.onClose);
  const companionOpen = useUiStateStore((state) => state.projectLeadCompanion.open);
  const companionMinimized = useUiStateStore((state) => state.projectLeadCompanion.minimized);
  if (!props.draft.draft) return null;
  const requestClose = () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    window.setTimeout(closeLatest, 0);
  };
  const shellLayout = !companionOpen
    ? "grid-rows-1 md:grid-cols-1"
    : companionMinimized
      ? "grid-rows-[minmax(0,1fr)_3.5rem] md:grid-cols-[minmax(0,1fr)_10rem] md:grid-rows-1"
      : "grid-rows-[minmax(0,1fr)_minmax(17rem,40%)] md:grid-cols-[minmax(0,1fr)_17.5rem] md:grid-rows-1 xl:grid-cols-[minmax(0,1fr)_19rem]";
  return (
    <Dialog open onOpenChange={(open) => handleDialogClose(open, requestClose)}>
      <DialogPopup
        data-project-lead-modal-shell
        showCloseButton={false}
        className="h-[min(50rem,calc(100dvh-3rem))] w-[calc(100vw-2rem)] max-w-[90rem] translate-y-0 scale-100 overflow-hidden border-0 bg-background p-2 shadow-none before:hidden max-sm:h-[calc(100dvh-1rem)] max-sm:w-full max-sm:max-h-none max-sm:rounded-t-2xl max-sm:border-0 max-sm:p-2 max-sm:pb-[max(0.5rem,env(safe-area-inset-bottom))]"
      >
        <div
          className={`grid min-h-0 flex-1 overflow-hidden rounded-xl border border-border ${shellLayout}`}
        >
          <div data-project-lead-workbench className="flex min-h-0 min-w-0 overflow-hidden bg-card">
            <ProjectLeadTaskWorkbench
              projectTitle={props.projectTitle}
              controller={props.draft}
              onClose={requestClose}
            />
          </div>
          <aside
            aria-label="Project Lead sidecar"
            data-project-lead-companion-portal
            data-project-lead-companion-sidecar
            className={
              companionOpen
                ? "min-h-0 min-w-0 overflow-hidden border-t border-border md:border-t-0 md:border-l"
                : "hidden min-h-0 min-w-0 overflow-hidden"
            }
          />
        </div>
      </DialogPopup>
    </Dialog>
  );
}

export function resolveProjectLeadUnavailableReason(input: {
  readonly hasProjectIdentity: boolean;
  readonly creationDisabled: boolean;
  readonly statusIsSupported: boolean;
  readonly hasAssignee: boolean;
  readonly hasMilestone: boolean;
  readonly hasParentTask: boolean;
  readonly hasDueDate: boolean;
  readonly hasDependencies: boolean;
}) {
  if (!input.hasProjectIdentity) return "Connect this CMMD project to a Forge project first.";
  if (input.creationDisabled)
    return "Task creation is read-only while the project workflow is unavailable.";
  const fields = [
    [!input.statusIsSupported, "status"],
    [input.hasAssignee, "assignee"],
    [input.hasMilestone, "milestone"],
    [input.hasParentTask, "parent task"],
    [input.hasDueDate, "due date"],
    [input.hasDependencies, "dependencies"],
  ] as const;
  const unsupported = fields.filter(([value]) => value).map(([, label]) => label);
  return unsupported.length
    ? `Project Lead does not yet support preset ${unsupported.join(", ")}. Clear those fields to continue.`
    : undefined;
}

export const isProjectLeadStatusSupported = (input: {
  readonly status: string;
  readonly workflowStageId: string | null;
  readonly defaultWorkflowStageId: string | null;
}) =>
  input.status === "todo" &&
  (input.workflowStageId === null || input.workflowStageId === input.defaultWorkflowStageId);

export const defaultTodoWorkflowStageId = (
  workflow: CmmdTaskWorkflow | null | undefined,
): string | null => workflow?.stages.find((stage) => stage.category === "todo")?.id ?? null;

export const markdownChangeHandler =
  (disabled: boolean, update: (value: string) => void) => (value: string) => {
    if (!disabled) update(value);
  };
export const statusChangeHandler =
  (
    workflow: CmmdTaskWorkflow | null | undefined,
    update: (value: ProjectTaskCreateStatusSelection) => void,
    resolve: (
      value: string | null,
      workflow: CmmdTaskWorkflow | null | undefined,
    ) => ProjectTaskCreateStatusSelection,
  ) =>
  (value: string | null) => {
    if (value !== null || workflow == null) update(resolve(value, workflow));
  };
export const visibleMoreProperties = (show: boolean, content: ReactNode) => (show ? content : null);
export const agentMembersOrEmpty = (members: readonly CmmdProjectAgentMember[] | undefined) =>
  members ?? [];
export function AgentMemberRosterError(props: { readonly error: string | null | undefined }) {
  return props.error ? (
    <span className="sr-only">Agent Members unavailable: {props.error}</span>
  ) : null;
}

export function ProjectLeadRestoreRetry(props: {
  readonly restoring: boolean;
  readonly error: string | null;
  readonly onRetry: () => void;
}) {
  return props.restoring && props.error !== null ? (
    <Button type="button" variant="outline" onClick={props.onRetry}>
      Retry restoring task draft
    </Button>
  ) : null;
}
